import React, { useState, useRef } from 'react';

const LogoPreview = ({ logoPath }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 192, height: 192 }); // Default size
  const popupRef = useRef(null);

  const handleMouseEnter = (event) => {
    setIsHovering(true);
    updatePosition(event);
  };
  
  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  
  const handleImageLoad = (event) => {
    // Get the natural dimensions once the image has loaded
    setNaturalSize({
      width: event.target.naturalWidth,
      height: event.target.naturalHeight,
    });
    // Recalculate position based on new dimensions
    if (isHovering) {
      updatePosition({ currentTarget: event.target.closest('.relative') });
    }
  };

  const updatePosition = (event) => {
    if (!event.currentTarget) return;
    
    // Get the position of the thumbnail image
    const rect = event.currentTarget.getBoundingClientRect();
    
    // Use the natural dimensions of the loaded image
    // Constrain popup size to a maximum (e.g., 192px), respecting aspect ratio
    const popupMax = 192;
    const { width: imgWidth, height: imgHeight } = naturalSize;
    
    let popupWidth, popupHeight;
    const aspectRatio = imgWidth / imgHeight;

    if (imgWidth > imgHeight) {
      popupWidth = Math.min(imgWidth, popupMax);
      popupHeight = popupWidth / aspectRatio;
    } else {
      popupHeight = Math.min(imgHeight, popupMax);
      popupWidth = popupHeight * aspectRatio;
    }

    // Position above and to the left
    let newX = rect.left - popupWidth - 10;
    let newY = rect.top - popupHeight - 10;

    // Collision detection and repositioning logic
    // Check for left-side clipping
    if (newX < 0) {
      newX = rect.right + 10; // Reposition to the right
      
      // If repositioning to the right still causes clipping, move to the left edge
      if (newX + popupWidth > window.innerWidth) {
        newX = 10; 
      }
    }

    // Check for top-side clipping (especially for first rows)
    if (newY < 0) {
      newY = rect.bottom + 10; // Reposition below the thumbnail
    }
    
    setPopupPosition({ x: newX, y: newY });
  };
  
  return (
    <div 
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={logoPath ? `/static/logos/${logoPath}` : "/static/logos/no-image.png"}
        alt="logo"
        className="w-8 h-8 rounded object-cover border border-gray-100 cursor-pointer"
      />
      {logoPath && isHovering && (
        <div
          ref={popupRef}
          className="fixed bg-white border border-gray-100 shadow-lg p-1 z-50 pointer-events-none"
          style={{
            top: popupPosition.y,
            left: popupPosition.x,
          }}
        >
          <img
            src={`/static/logos/${logoPath}`}
            alt="logo preview"
            onLoad={handleImageLoad}
            className="max-h-48 max-w-48 object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default LogoPreview;